export type CylinderPosition = 'single' | 'bottom' | 'middle' | 'top';

export type CylinderBarShapeProps = {
   x?: number;
   y?: number;
   width?: number;
   height?: number;
   color: string;
   transparent: boolean;
   position?: CylinderPosition | null;
};

export function getCylinderPosition(
   row: Record<string, unknown> | null | undefined,
   currentKey: string,
   orderedStackKeys: readonly string[],
): CylinderPosition | null {
   if (!row) {
      return null;
   }

   const visibleKeys = orderedStackKeys.filter(key => {
      const value = Number(row[key]);
      return Number.isFinite(value) && value > 0;
   });
   const index = visibleKeys.indexOf(currentKey);

   if (index === -1) {
      return null;
   }

   if (visibleKeys.length === 1) {
      return 'single';
   }

   if (index === 0) {
      return 'bottom';
   }

   if (index === visibleKeys.length - 1) {
      return 'top';
   }

   return 'middle';
}

function parseHslColor(color: string) {
   const match = color.match(
      /hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*(?:,\s*([\d.]+)\s*)?\)/i,
   );

   if (!match) {
      return null;
   }

   return {
      hue: Number(match[1]),
      saturation: Number(match[2]),
      lightness: Number(match[3]),
      alpha: match[4] ? Number(match[4]) : 1,
   };
}

function toHsla(
   color: string,
   options: { lightnessShift?: number; alpha?: number },
) {
   const parsed = parseHslColor(color);

   if (!parsed) {
      return color;
   }

   const nextLightness = Math.max(
      0,
      Math.min(100, parsed.lightness + (options.lightnessShift ?? 0)),
   );
   const nextAlpha = Math.max(0, Math.min(1, options.alpha ?? parsed.alpha));

   return `hsla(${parsed.hue}, ${parsed.saturation}%, ${nextLightness}%, ${nextAlpha})`;
}

function buildFrontGradient(
   baseColor: string,
   centerShift: number,
   opacity: number,
) {
   return {
      edge: toHsla(baseColor, { alpha: opacity }),
      center: toHsla(baseColor, {
         lightnessShift: centerShift,
         alpha: opacity,
      }),
   };
}

export default function CylinderBarShape({
   x = 0,
   y = 0,
   width = 0,
   height = 0,
   color,
   transparent,
   position = 'single',
}: CylinderBarShapeProps) {
   if (width <= 0 || height <= 0) {
      return null;
   }

   const radiusX = width / 2;
   const radiusY = Math.min(Math.max(width * 0.18, 6), 12);
   const showTopCap =
      position === 'single' || position === 'bottom' || position === 'top';
   const showBottomCap = position === 'single' || position === 'bottom';
   const topCenterY = y + radiusY;
   const bottomCenterY = y + height - radiusY;
   const shellBodyY = showTopCap ? topCenterY : y;
   const shellBodyBottomY = showBottomCap ? bottomCenterY : y + height;
   const shellBodyHeight = Math.max(shellBodyBottomY - shellBodyY, 0);
   const indentedBodyPath = [
      `M ${x} ${shellBodyY}`,
      `H ${x + width}`,
      `V ${y + height + radiusY}`,
      `A ${radiusX} ${radiusY} 0 0 0 ${x} ${y + height + radiusY}`,
      'Z',
   ].join(' ');
   const fillColor = transparent ? 'transparent' : color;

   const shellGradient = buildFrontGradient(color, -8, 0.28);
   const fillGradient = buildFrontGradient(fillColor, -10, 1);
   const fillTopColor = toHsla(fillColor, {
      lightnessShift: -14,
      alpha: 1,
   });
   const shellTopColor = toHsla(color, {
      lightnessShift: 12,
      alpha: 0.72,
   });
   const shellTopFrontColor = toHsla(color, {
      lightnessShift: -4,
      alpha: 0.12,
   });
   const fillBottomColor = toHsla(fillColor, {
      lightnessShift: -6,
      alpha: 1,
   });

   const idSuffix = `${Math.round(x)}-${Math.round(y)}-${Math.round(width)}-${Math.round(height)}`;
   const shellGradientId = `shell-cylinder-${idSuffix}`;
   const fillGradientId = `fill-cylinder-${idSuffix}`;
   const shellTopHalfClipId = `shell-top-half-${idSuffix}`;
   const shellBottomHalfClipId = `shell-bottom-half-${idSuffix}`;

   return (
      <g>
         <defs>
            <linearGradient
               id={shellGradientId}
               x1="0%"
               y1="0%"
               x2="100%"
               y2="0%"
            >
               <stop offset="0%" stopColor={shellGradient.edge} />
               <stop offset="50%" stopColor={shellGradient.center} />
               <stop offset="100%" stopColor={shellGradient.edge} />
            </linearGradient>
            <linearGradient
               id={fillGradientId}
               x1="0%"
               y1="0%"
               x2="100%"
               y2="0%"
            >
               <stop offset="0%" stopColor={fillGradient.edge} />
               <stop offset="50%" stopColor={fillGradient.center} />
               <stop offset="100%" stopColor={fillGradient.edge} />
            </linearGradient>
            <clipPath id={shellTopHalfClipId}>
               <rect
                  x={x}
                  y={topCenterY - radiusY}
                  width={width}
                  height={radiusY}
               />
            </clipPath>
            <clipPath id={shellBottomHalfClipId}>
               <rect x={x} y={bottomCenterY} width={width} height={radiusY} />
            </clipPath>
         </defs>

         {showBottomCap && (
            <ellipse
               cx={x + radiusX}
               cy={bottomCenterY}
               rx={radiusX}
               ry={radiusY}
               fill={toHsla(color, { lightnessShift: -10, alpha: 0.18 })}
            />
         )}

         {showBottomCap ? (
            <rect
               x={x}
               y={shellBodyY}
               width={width}
               height={shellBodyHeight}
               fill={`url(#${shellGradientId})`}
            />
         ) : (
            <path d={indentedBodyPath} fill={`url(#${shellGradientId})`} />
         )}

         {showBottomCap && (
            <ellipse
               cx={x + radiusX}
               cy={bottomCenterY}
               rx={radiusX}
               ry={radiusY}
               fill={toHsla(color, { lightnessShift: -14, alpha: 0.24 })}
               clipPath={`url(#${shellBottomHalfClipId})`}
            />
         )}

         {showTopCap && (
            <>
               <ellipse
                  cx={x + radiusX}
                  cy={topCenterY}
                  rx={radiusX}
                  ry={radiusY}
                  fill={shellTopColor}
               />
               <ellipse
                  cx={x + radiusX}
                  cy={topCenterY}
                  rx={radiusX}
                  ry={radiusY}
                  fill={shellTopFrontColor}
                  clipPath={`url(#${shellTopHalfClipId})`}
               />
            </>
         )}

         {showBottomCap && (
            <ellipse
               cx={x + radiusX}
               cy={bottomCenterY}
               rx={radiusX}
               ry={radiusY}
               fill={fillBottomColor}
            />
         )}
         {showBottomCap ? (
            <rect
               x={x}
               y={shellBodyY}
               width={width}
               height={shellBodyHeight}
               fill={`url(#${fillGradientId})`}
            />
         ) : (
            <path d={indentedBodyPath} fill={`url(#${fillGradientId})`} />
         )}
         {showTopCap && (
            <ellipse
               cx={x + radiusX}
               cy={topCenterY}
               rx={radiusX}
               ry={radiusY}
               fill={fillTopColor}
            />
         )}
      </g>
   );
}
